<template>
  <div
    class="d-flex align-items-center"
  >
    <div
      v-if="!noCheckbox"
      class="checkbox set-row set-row-hover"
      :class="{ last }"
      @click="$emit('toggle')"
    >
      <Checkbox
        :value="checked"
        :emit="false"
      />
    </div>

    <div
      class="d-flex flex-wrap flex-md-nowrap set-row flex-grow-1 set-row-hover"
      :class="{ selected, last }"
      @click="$emit('select')"
    >
      <template v-for="c in columnsWithNames">
        <div
          v-if="c.key === 'day' && !reserveQueue"
          :key="`day-${c.key}`"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="money-font small font-weight-normal px-1"
        >
          {{ order.formattedDay }}
        </div>
        <div
          v-if="c.key === 'month' && !reserveQueue"
          :key="`month-${c.key}`"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="money-font small font-weight-normal px-1"
        >
          {{ order.formattedMonth }}
        </div>

        <div
          v-else-if="c.key === 'name'"
          :key="`name-${c.key}`"
          :class="c.key"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="flex-grow-1 px-1 w-100"
          style="height: 32px;"
        >
          <PayerDetailsPopover
            v-if="payer"
            :id="payer.id"
            trigger="mouseenter"
            :delay="[1200, 0]"
            class="text-truncate w-100"
          >
            <PayerName
              v-bind="payer"
              class="text-truncate"
              no-code
            />
          </PayerDetailsPopover>
        </div>
        <div
          v-else-if="c.key === 'code'"
          :key="`code-${c.key}`"
          v-tippy="{
            trigger: (payer?.code || '').length > 15 ? 'mouseenter' : 'manual',
            delay: 1200
          }"
          :class="c.key"
          :content="payer?.code"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="text-secondary align-self-center small px-1 text-truncate"
        >
          {{ payer?.code }}
        </div>
        <div
          v-else-if="c.type === 'property'"
          :key="`property-${c.key}`"
          v-tippy="{
            trigger: (payer?.properties[c.key] || '').length > 25 ? 'mouseenter' : 'manual',
            delay: 1200
          }"
          :content="payer?.properties[c.key]"
          style="width: 150px"
          class="px-1 small offer-name"
        >
          {{ payer?.properties[c.key] }}
        </div>
        <div
          v-else-if="c.type === 'group'"
          :key="`group-${c.key}`"
          style="width: 100px"
          class="px-1"
        >
          <i
            v-if="payer?.groups.includes(c.key)"
            class="fas fa-square-check text-primary"
          />
          <i
            v-else
            class="fa-solid fa-square check uncheck"
          />
        </div>

        <span
          v-else-if="c.key === 'productSetName'"
          :key="`productSetName-${c.key}`"
          :class="c.key"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="offer-name px-1"
        >
          {{ order.listingName || order.set.name }}
        </span>
        <span
          v-else-if="c.key === 'position'"
          :key="`position-${c.key}`"
          :class="c.key"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="offer-name px-1"
        >
          {{ order.position + 1 }}
        </span>

        <span
          v-else-if="c.key === 'aggregatedPrice'"
          :key="`aggregatedPrice-${c.key}`"
          :class="c.key"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="aggregation px-1 money-font text-right"
        >
          {{ formatCurrency(order.totalPrice, order.currency) }}
        </span>
        <span
          v-else-if="c.key === 'aggregatedCount'"
          :key="`aggregatedCount-${c.key}`"
          :class="c.key"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="aggregation px-1 money-font text-right"
        >
          <i class="far fa-box mr-2" />
          x{{ order.totalCount }}
        </span>

        <span
          v-else-if="c.key === 'status'"
          :key="`status-${c.key}`"
          :class="c.key"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="status-name px-1"
        >
          <span
            class="badge px-3"
            :class="order.status ? 'text-white' : ''"
            :style="{ backgroundColor: order.status ? colors(order.status.color) : 'transparent' }"
          >
            {{ order.status ? order.status.name : '-' }}
          </span>
        </span>

        <span
          v-else-if="c.key === 'created'"
          :key="`created-${c.key}`"
          :class="c.key"
          class="px-1 date"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
        >
          {{ formatDate(order.created) }}
        </span>

        <div
          v-else-if="c.key === 'paid'"
          :key="`paid-${c.key}`"
          :class="c.key"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="px-1"
        >
          <span
            v-if="order.isPaid"
            class="text-success small text-uppercase"
          >
            <span
              class="fa-stack fa-1x"
              style="width: 18px"
            >
              <i class="far fa-circle-dollar fa-stack-1x" />
            </span>

            {{ $t('general.yes') }}
          </span>
          <span
            v-else
            class="text-danger small text-uppercase"
          >
            <span
              class="fa-stack fa-1x"
              style="width: 18px"
            >
              <i class="far fa-circle-dollar fa-stack-1x" />
              <i class="far fa-slash fa-stack-1x" />
            </span>
            <span>
              {{ $t('general.no') }}
            </span>
          </span>
        </div>
        <div
          v-else-if="c.key === 'comment'"
          :key="`comment-${c.key}`"
          :class="c.key"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="px-1 comment text-truncate"
        >
          {{ order.comment || '-' }}
        </div>
        <div
          v-else-if="c.key === 'timetable'"
          :key="`timetable-${c.key}`"
          :class="c.key"
          :style="c.width ? { width: `${c.width}px`, minWidth: `${c.width}px`, maxWidth: `${c.width}px` } : {}"
          class="px-1 timetable-cell"
        >
          <TimetableCell
            :order="order"
          />
        </div>
        <div
          v-else-if="c.key === 'delivered'"
          :key="`delivered-${c.key}`"
          :class="c.key"
          class="px-1"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
        >
          <span
            v-if="order.delivered"
            class="text-success small text-uppercase"
          >
            <span
              class="fa-stack fa-1x"
              style="width: 18px"
            >
              <i class="far fa-hand-holding-box fa-stack-1x" />
            </span>

            {{ $t('general.yes') }}
          </span>
          <span
            v-else
            class="text-danger small text-uppercase"
          >
            <span
              class="fa-stack fa-1x"
              style="width: 18px"
            >
              <i class="far fa-hand-holding-box fa-stack-1x" />
              <i class="far fa-slash fa-stack-1x" />
            </span>
            <span>
              {{ $t('general.no') }}
            </span>
          </span>
        </div>
        <div
          v-else-if="c.key === 'vat'"
          :key="`vat-${c.key}`"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="px-1 small offer-name"
        >
          {{ formatVatCode(order.listing?.vatCode || '') }}
        </div>
        <div
          v-else-if="c.key === 'accountingCode'"
          :key="`accountingCode-${c.key}`"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="px-1 small offer-name"
        >
          {{ order.listing?.accountingCode || '-' }}
        </div>
        <div
          v-else-if="c.key === 'accountNumber'"
          :key="`accountNumber-${c.key}`"
          v-tippy="{
            trigger: (order.listing?.accountNumber || '').length > 20 ? 'mouseenter' : 'manual',
            delay: 500
          }"
          :content="formatAccountNumber(order.listing?.accountNumber || '-')"
          :style="c.width ? { width: `${c.width}px` } : {}"
          class="px-1 small offer-name text-truncate"
        >
          {{ formatAccountNumber(order.listing?.accountNumber || '-') }}
        </div>
        <div
          v-else-if="c.type === 'listing-field'"
          :key="c.key"
          v-tippy="{
            trigger: (order.listing?.fields?.[c.key.split(':')[1]] || '').length > 20 ? 'mouseenter' : 'manual',
            delay: 500
          }"
          :content="order.listing?.fields?.[c.key.split(':')[1]]"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="px-1 small offer-name"
          :class="{ 'below-minimum': isBelowMinimum(c, order) }"
        >
          <template v-if="isMinCountField(c)">
            <i
              v-if="isBelowMinimum(c, order)"

              class="far fa-triangle-exclamation pr-1"
            />
            <i
              v-else
              class="fas fa-check pr-1"
            />
          </template>
          {{ order.listing?.fields?.[c.key.split(':')[1]] || '-' }}
        </div>
        <div
          v-else-if="c.type === 'listing-assignment'"
          :key="`listing-assignment-${c.key}`"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
          class="px-1 small offer-name"
        >
          {{ listingAssignmentNames(order.listing, c.key.split(':')[1]) || '-' }}
        </div>
        <div
          v-else-if="c.key === 'space'"
          :key="`space-${c.key}`"
          class="flex-grow-1 align-self-center"
        >
          &nbsp;
        </div>
        <div
          v-else-if="c.key === 'count'"
          :key="`count-${c.key}`"
          :class="c.key"
          class="money-font text-right px-2"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
        >
          <span>{{ order.count }}</span>
        </div>
        <div
          v-else-if="c.key === 'discount'"
          :key="`discount-${c.key}`"
          :class="c.key"
          class="text-right discount px-1"
          :style="c.width ? { minWidth: `${c.width}px` } : {}"
        >
          {{ order.discountName }}
        </div>
        <div
          v-else-if="c.key === 'price'"
          :key="`price-${c.key}`"
          :class="c.key"
          class="money-font column-divider text-right d-flex align-items-center justify-content-end pr-2"
        >
          <span v-if="order.discountName">
            <Tippy>

              <template #trigger>
                <i class="fas fa-badge-percent text-primary pr-2" />
              </template>

              {{ order.discountName }}
              <div class="text-strikethrough">
                {{ formatCurrency(order.priceBeforeDiscount, order.currency) }}
              </div>

            </Tippy>
          </span>

          {{ formatCurrency(order.unitPrice, order.currency) }}
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import PayerDetailsPopover from '@/components/payers/PayerDetailsPopover';
import PayerName from '@/components/payers/PayerName';
import TimetableCell from '@/components/shop/report/table/cells/TimetableCell';
import { dictionary } from '@/pages/shop/order/columns';
import colors from '@/utils/colors';
import formatVatCode from '@/utils/vat';
import moment from 'moment';
import { accountNumber } from '@/utils/filters';
import { mapGetters } from 'vuex';

export default {
  props: {
    checked: Boolean,
    selected: Boolean,
    reserveQueue: Boolean,
    columns: Array,
    groupBy: String,
    order: Object,
    payer: Object,
    last: Boolean,
    noCheckbox: Boolean,
  },
  data: () => ({
    formatVatCode,
  }),
  components: {
    PayerDetailsPopover,
    PayerName,
    TimetableCell,
  },
  computed: {
    ...mapGetters(['formatCurrency']),
    ...mapGetters('shop', [
      'singleOrderSchema',
      'catalogDetails',
    ]),
    columnsWithNames() {
      return this.columns.map((c) => {
        const orderColumn = dictionary[c.key];
        if (!orderColumn) return c;
        return {
          ...c,
          ...orderColumn,
          width: c.width || orderColumn.width,
        };
      });
    },
  },
  methods: {
    colors,
    formatAccountNumber: accountNumber,
    formatDate(date) {
      return moment(date).format('DD MMM YYYY HH:mm');
    },
    isMinCountField(column) {
      const fieldId = column.key.split(':')[1];
      const fieldDef = (this.catalogDetails.listingFields || []).find((f) => f.fieldId === fieldId);
      return fieldDef && fieldDef.type === 'MinCount';
    },
    isBelowMinimum(column, order) {
      if (!this.isMinCountField(column)) return false;
      const fieldId = column.key.split(':')[1];
      const minValue = Number.parseInt(order.listing?.fields?.[fieldId], 10);
      if (Number.isNaN(minValue) || minValue <= 0) return false;
      return (order.totalCount || 0) < minValue;
    },
    listingAssignmentNames(listing, subCatalogId) {
      if (!listing?.assignments) return null;
      const nodeIds = listing.assignments
        .filter((a) => a.subCatalogId === subCatalogId)
        .map((a) => a.nodeId);
      if (nodeIds.length === 0) return null;
      return nodeIds
        .map((nodeId) => this.catalogDetails.subCatalogNodes?.find((n) => n.nodeId === nodeId)?.name)
        .filter((x) => x)
        .join(', ');
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "./style.scss";

  .date {
    font-size: 12px;
  }

  .status-name {
    .badge {
      font-size: 10px;
    }
  }

  .discount {
    max-height: 32px;
    line-height: 0.9rem;
    height: 1.8rem;
    font-size: 12px;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .set-row-hover:hover {
    background-color: rgba(100, 100, 100, 0.1);
  }

  .offer-name {
    font-size: 12px;
    font-weight: 500;
    line-height: 1.1;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  .below-minimum {
    background-color: rgba(220, 53, 69, 0.15);
    color: #dc3545;
    border-radius: 4px;
    margin-top: -5px;
    margin-bottom: -5px;
    padding-top: 5px;
    padding-bottom: 5px;
  }

  .aggregation {
    font-size: 13px;
    line-height: 1.2;
  }

  .timetable-cell {
    min-width: 0;
    overflow: hidden;
    text-align: left;
  }

  .timetable-cell :deep(div) {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .aggregatedPrice,
  .aggregatedCount {
    height: 30px;
    display: flex;
    align-items: center;
    background-color: #fafafa;
  }

  .aggregatedCount {
    border-left: 1px solid #ddd;
    padding-left: 10px !important;
  }

  .aggregatedPrice {
    border-right: 1px solid #ddd;
    padding-right: 10px !important;
    margin-right: 10px !important;
    justify-content: flex-end;
  }

  .set-row {
    border-top: 1px solid #ddd;
    padding: 2px 0px;
    cursor: pointer;
    height: 32px;
    align-items: center;

    &.last {
      border-bottom: 1px solid #ddd;
    }

    &.no-padding {
      padding: 0px;
    }

    &.checkbox {
      border-right: 1px solid rgba(100, 100, 100, 0.2);
      display: flex;
      justify-content: center;
    }

    &.selected {
      background-color: rgba($blue, 0.2);
    }
  }

  .comment {
    font-size: 12.8px;
  }

  .check {
    font-size: 1rem;
  }

  .uncheck {
    color: #f6f6f6
  }

  .payer-icon {
    color: $light-gray;
  }
</style>
